import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAccountData } from './AccountContext';
import {
  addTrackToPlaylistList,
  createPlaylistInList,
  deletePlaylistFromList,
  duplicatePlaylistInList,
  loadUserPlaylists,
  moveTrackInPlaylistList,
  removeTrackFromPlaylistList,
  reorderTracksInPlaylistList,
  saveUserPlaylists,
  updatePlaylistInList,
} from '../utils/studioPlaylistStorage';
import { getStudioPlaylists, updateStudioPlaylists } from '../utils/studioPlaylistsApi';
import { isTrackInPlaylist, trackFromBookmark } from '../utils/studioPlaylistItems';

const StudioPlaylistsContext = createContext(null);

const SYNC_DELAY_MS = 800;

export function StudioPlaylistsProvider({ children }) {
  const { auth, token } = useAccountData();
  const userId = auth?.id || auth?._id || auth?.email || null;
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const syncTimer = useRef(null);
  const playlistsRef = useRef([]);

  useEffect(() => {
    playlistsRef.current = playlists;
  }, [playlists]);

  const persistLocal = useCallback(
    (next) => {
      saveUserPlaylists(userId, next);
      setPlaylists(next);
    },
    [userId],
  );

  const syncToServer = useCallback(
    async (next) => {
      if (!token) return;
      setSyncing(true);
      try {
        const saved = await updateStudioPlaylists(token, next);
        const savedArr = Array.isArray(saved) ? saved : null;
        const nextIds = new Set(next.map((p) => p.id));
        // The server is a per-user mirror (MongoDB in vercel-backend). Only
        // adopt its response when it actually echoes back every playlist we
        // just sent — otherwise a stale/empty server response would silently
        // wipe a playlist the user just created. Local (offline) state stays
        // authoritative in that case.
        const serverHasAll =
          savedArr && (nextIds.size === 0 || next.every((p) => savedArr.some((s) => s.id === p.id)));
        if (savedArr && serverHasAll) {
          saveUserPlaylists(userId, savedArr);
          setPlaylists(savedArr);
        } else if (savedArr) {
          // server returned a partial list (missing our new playlist) — keep
          // the local copy as the source of truth and re-persist it.
          saveUserPlaylists(userId, next);
        }
      } catch (err) {
        console.warn('Studio playlists sync failed:', err?.message || err);
      } finally {
        setSyncing(false);
      }
    },
    [token, userId],
  );

  const scheduleSync = useCallback(
    (next) => {
      if (!token) return;
      if (syncTimer.current) clearTimeout(syncTimer.current);
      syncTimer.current = setTimeout(() => {
        syncToServer(next);
      }, SYNC_DELAY_MS);
    },
    [token, syncToServer],
  );

  const applyPlaylists = useCallback(
    (next, { sync = true } = {}) => {
      // Keep the ref synchronously fresh so chained mutations in the same
      // event handler (e.g. createPlaylist() immediately followed by
      // addTrackToPlaylist(pl.id, ...)) read the latest list instead of the
      // stale value from the previous render. Without this, addTrackToPlaylist
      // finds no `pl.id`, returns the pre-create list, and setPlaylists wipes
      // the just-created playlist.
      playlistsRef.current = next;
      persistLocal(next);
      if (sync && token) scheduleSync(next);
    },
    [persistLocal, scheduleSync, token],
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        if (token) {
          const remote = await getStudioPlaylists(token);
          if (!cancelled) {
            const local = loadUserPlaylists(userId);
            const merged = remote?.length ? remote : local;
            saveUserPlaylists(userId, merged);
            setPlaylists(merged);
            if (local.length && !remote?.length) {
              scheduleSync(local);
            }
          }
        } else {
          if (!cancelled) setPlaylists(loadUserPlaylists(userId));
        }
      } catch (err) {
        console.warn('Studio playlists load failed:', err?.message || err);
        if (!cancelled) setPlaylists(loadUserPlaylists(userId));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
      if (syncTimer.current) clearTimeout(syncTimer.current);
    };
  }, [userId, token, scheduleSync]);

  const createPlaylist = useCallback(
    ({ name, description = '', regionId = '' } = {}) => {
      const [playlist, next] = createPlaylistInList(playlistsRef.current, {
        name,
        description,
        regionId,
      });
      applyPlaylists(next);
      return playlist;
    },
    [applyPlaylists],
  );

  const deletePlaylist = useCallback(
    (playlistId) => {
      const next = deletePlaylistFromList(playlistsRef.current, playlistId);
      applyPlaylists(next);
    },
    [applyPlaylists],
  );

  const addTrackToPlaylist = useCallback(
    (playlistId, track) => {
      const next = addTrackToPlaylistList(playlistsRef.current, playlistId, track);
      applyPlaylists(next);
      return next.find((p) => p.id === playlistId)?.tracks.slice(-1)[0] || null;
    },
    [applyPlaylists],
  );

  const renamePlaylist = useCallback(
    (playlistId, patch) => {
      const next = updatePlaylistInList(playlistsRef.current, playlistId, patch);
      applyPlaylists(next);
      return next.find((p) => p.id === playlistId) || null;
    },
    [applyPlaylists],
  );

  const duplicatePlaylist = useCallback(
    (playlistId) => {
      const next = duplicatePlaylistInList(playlistsRef.current, playlistId);
      const sourceIdx = playlistsRef.current.findIndex((p) => p.id === playlistId);
      applyPlaylists(next);
      return next[sourceIdx + 1] || null;
    },
    [applyPlaylists],
  );

  const moveTrack = useCallback(
    (playlistId, trackId, delta) => {
      const next = moveTrackInPlaylistList(playlistsRef.current, playlistId, trackId, delta);
      if (next === playlistsRef.current) return false;
      applyPlaylists(next);
      return true;
    },
    [applyPlaylists],
  );

  const reorderTracks = useCallback(
    (playlistId, fromIndex, toIndex) => {
      const next = reorderTracksInPlaylistList(playlistsRef.current, playlistId, fromIndex, toIndex);
      if (next === playlistsRef.current) return false;
      applyPlaylists(next);
      return true;
    },
    [applyPlaylists],
  );

  const addBookmarkToPlaylist = useCallback(
    (playlistId, bookmark) => {
      const track = trackFromBookmark(bookmark);
      if (!track) return null;
      return addTrackToPlaylist(playlistId, track);
    },
    [addTrackToPlaylist],
  );

  const createPlaylistAndAdd = useCallback(
    (name, bookmark, { description = '', regionId = '' } = {}) => {
      const track = trackFromBookmark(bookmark);
      if (!track) return null;
      const [playlist, withPl] = createPlaylistInList(playlistsRef.current, {
        name,
        description,
        regionId,
      });
      const next = addTrackToPlaylistList(withPl, playlist.id, track);
      applyPlaylists(next);
      return playlist;
    },
    [applyPlaylists],
  );

  const removeTrackFromPlaylist = useCallback(
    (playlistId, trackId) => {
      const next = removeTrackFromPlaylistList(playlistsRef.current, playlistId, trackId);
      applyPlaylists(next);
    },
    [applyPlaylists],
  );

  const findPlaylistsContaining = useCallback(
    (bookmark) => {
      const trackMeta = trackFromBookmark(bookmark);
      if (!trackMeta) return [];
      return playlists.filter((pl) => isTrackInPlaylist(pl, trackMeta));
    },
    [playlists],
  );

  const value = useMemo(
    () => ({
      playlists,
      loading,
      syncing,
      createPlaylist,
      deletePlaylist,
      renamePlaylist,
      duplicatePlaylist,
      addTrackToPlaylist,
      addBookmarkToPlaylist,
      createPlaylistAndAdd,
      removeTrackFromPlaylist,
      moveTrack,
      reorderTracks,
      findPlaylistsContaining,
      trackFromBookmark,
    }),
    [
      playlists,
      loading,
      syncing,
      createPlaylist,
      deletePlaylist,
      renamePlaylist,
      duplicatePlaylist,
      addTrackToPlaylist,
      addBookmarkToPlaylist,
      createPlaylistAndAdd,
      removeTrackFromPlaylist,
      moveTrack,
      reorderTracks,
      findPlaylistsContaining,
    ],
  );

  return (
    <StudioPlaylistsContext.Provider value={value}>{children}</StudioPlaylistsContext.Provider>
  );
}

export function useStudioPlaylists() {
  const ctx = useContext(StudioPlaylistsContext);
  if (!ctx) {
    throw new Error('useStudioPlaylists must be used within StudioPlaylistsProvider');
  }
  return ctx;
}

export function useStudioPlaylistsOptional() {
  return useContext(StudioPlaylistsContext);
}
